import {
  Box,
  SimpleGrid,
  Text,
  Flex,
  HStack,
  Badge,
  Icon,
  Circle
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { CalendarIcon } from "@chakra-ui/icons";
import { MdAccessTime, MdCheck } from "react-icons/md";
import moment from 'moment-timezone';
import { fetchBays } from '../apis/baysApi';

export default function ListBays() {
  const [bays, setBays] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      let date = new Date();
      let currentHour = date.getHours();
      
      //console.log("currentTime", currentTime);
      let formattedTime = moment.utc(currentTime).tz('America/Chicago').format('YYYY-MM-DD HH:mm:ssZ');
      
      try {
        const response = await fetchBays(formattedTime);
        //console.log("formattedTime", formattedTime);
        const groupedBays = groupAndSortBays(response);
        //console.log('groupedBays', groupedBays);
        setBays(groupedBays);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchData();
  
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
  
    return () => clearInterval(timer);
  }, []);

  const groupAndSortBays = (bays) => {
    const currentDate = new Date();
    const groupedBays = bays.reduce((acc, bay) => {
      if (!acc[bay.bayId]) {
        acc[bay.bayId] = [];
      }
      acc[bay.bayId].push(bay);
      return acc;
    }, {});
  
    return Object.values(groupedBays).map(bayGroup => {
      // Filter out bays with occupiedEndTime in the past
      const filteredBayGroup = bayGroup.filter(bay => {
        const endTime = bay.occupiedEndTime ? new Date(bay.occupiedEndTime) : new Date(0);
        return endTime >= currentDate;
      });
  
      // Sort the remaining bays by occupiedStartTime
      const sortedBayGroup = filteredBayGroup.sort((a, b) => {
        const aStartTime = a.occupiedStartTime ? new Date(a.occupiedStartTime) : new Date(0);
        const bStartTime = b.occupiedStartTime ? new Date(b.occupiedStartTime) : new Date(0);
        return aStartTime - bStartTime;
      });
  
      // If no bays are available after filtering, return the first bay from the original group
      return sortedBayGroup.length > 0 ? sortedBayGroup[0] : bayGroup[0];
    });
  };

  const formatBookedHours = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    
    const formattedStart = moment(startTime).format('h:mmA').replace(':00', '');
    const formattedEnd = moment(endTime).format('h:mmA').replace(':00', '');
    
    return `${formattedStart} - ${formattedEnd}`;
  };

  return (
    <Box maxW="7xl" mx={'auto'} pt={2.5} px={{ base: 2, sm: 12, md: 17 }} paddingBottom={250}>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, lg: 8 }}>
        {bays.map((bay) => {
          if (!bay) return null;
  
          const currentDate = new Date();
          const currentHour = currentDate.getHours();
          const currentMinutes = currentDate.getMinutes();
          const occupiedStartTime = bay.occupiedStartTime ? moment(bay.occupiedStartTime).toDate() : null;
          const occupiedEndTime = bay.occupiedEndTime ? moment(bay.occupiedEndTime).toDate() : null;
          const nextAvailableInHours = occupiedStartTime ? occupiedStartTime.getHours() - currentHour : null;
          const nextAvailableInMinutes = nextAvailableInHours !== null ? (occupiedStartTime.getHours() * 60 - (currentHour * 60 + currentMinutes)) : null;
  
          return (
            <Box 
              key={bay.bayId} 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              p={4}
              bg="rgba(130, 202, 156, 0.15)"
              borderRadius="lg"
              border="1px solid"
              borderColor={bay.isAvailable ? "#82ca9c" : "#ff8a8a"}
              transition="all 0.2s"
              _hover={{
                borderColor: bay.isAvailable ? "#5ba97c" : "#ff6b6b",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)"
              }}
            >
              <Flex alignItems="center">
                <Flex
                  borderRadius="full"
                  width="40px"
                  height="40px"
                  bg={bay.isAvailable ? "#82ca9c" : "#ff8a8a"}
                  color="white"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  marginRight="1em"
                  border="1px solid"
                  borderColor={bay.isAvailable ? "#5ba97c" : "#ff6b6b"}
                >
                  <Text fontSize="xs" fontWeight="medium">Bay</Text>
                  <Text fontSize="sm" fontWeight="bold">{bay.bayId}</Text>
                </Flex>
                <Box>
                  <Badge 
                    colorScheme={bay.isAvailable ? "green" : "red"}
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    mb={1}
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    fontWeight="medium"
                    variant="subtle"
                    display="inline-flex"
                    alignItems="center"
                  >
                    <Box 
                      w="6px" 
                      h="6px" 
                      borderRadius="full" 
                      bg={bay.isAvailable ? "green.500" : "red.500"} 
                      mr={1.5}
                    />
                    {bay.isAvailable ? 'Available' : bay.isBooking ? 'Active Booking' : 'Active Session'}
                  </Badge>
                  
                  {!bay.isAvailable && bay.occupiedEndTime && (
                    <HStack spacing={2} mt={1}>
                      <Icon as={CalendarIcon} color="red.500" boxSize={3} />
                      <Text color="gray.700" fontWeight="medium" fontSize="xs">
                        Booked: {formatBookedHours(bay.occupiedStartTime, bay.occupiedEndTime)}
                      </Text>
                    </HStack>
                  )}
                  
                  {bay.isAvailable && bay.occupiedEndTime && nextAvailableInHours >= 0 && (
                    <HStack spacing={2} mt={1}>
                      <Icon as={CalendarIcon} color="green.500" boxSize={3} />
                      <Text color="gray.600" fontSize="xs" fontWeight="medium">
                        Next booking {nextAvailableInHours === 0 && nextAvailableInMinutes <= 0 
                          ? 'is Now' 
                          : nextAvailableInMinutes < 60 
                            ? `in ${nextAvailableInMinutes} minute(s)` 
                            : `in ${nextAvailableInHours} hour(s)`}
                      </Text>
                    </HStack>
                  )}
                </Box>
              </Flex>
              
              {bay.isAvailable ? (
                <Circle 
                  size="28px" 
                  bg="#82ca9c" 
                  color="white"
                  boxShadow="0 1px 3px rgba(130, 202, 156, 0.3)"
                  transition="all 0.2s"
                  _hover={{
                    transform: "scale(1.05)",
                    boxShadow: "0 2px 4px rgba(130, 202, 156, 0.4)"
                  }}
                >
                  <Icon as={MdCheck} boxSize={3.5} fontWeight="bold" />
                </Circle>
              ) : (
                <Circle 
                  size="28px" 
                  bg="#ff8a8a" 
                  color="white"
                  boxShadow="0 1px 3px rgba(255, 138, 138, 0.3)"
                  transition="all 0.2s"
                  _hover={{
                    transform: "scale(1.05)",
                    boxShadow: "0 2px 4px rgba(255, 138, 138, 0.4)"
                  }}
                >
                  <Icon as={MdAccessTime} boxSize={3.5} />
                </Circle>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}